/**
 * AI Model Pricing — typed client helpers.
 *
 * Mirrors the response shapes from
 * `fullgerman_backend/src/types/ai-model-pricing.types.ts` exactly.
 */
import { api } from '@/lib/api';

export type ModelCategory = 'text' | 'image';
export type ModelStatus = 'live' | 'degraded' | 'unavailable';

export interface ModelStatusEntry {
  modelId: string;
  category: ModelCategory | null;
  currentPricePerMTokensUsd?: number | null;
  currentPricePerImageUsd?: number | null;
  availableUntil: string | null;
  replacement: string | null;
  lastProbedAt: string | null;
  lastProbeOk: boolean | null;
  lastLatencyMs: number | null;
  daysSinceLastProbe: number | null;
  status: ModelStatus;
}

export interface ModelsStatusResponse {
  models: ModelStatusEntry[];
}

export interface ProbeHistoryPoint {
  probedAt: string;
  ok: boolean;
  latencyMs: number;
}

export interface TierRecommendation {
  tierName: string;
  currency: string;
  currentPriceCents: number;
  avgCostPerUserMonthUsd: number;
  targetMarginPercent: number;
  recommendedPriceCents: number;
  deltaCents: number;
  breakdown: {
    totalCostUsdLast30Days: number;
    distinctUsersWithCost: number;
    notes: string;
  };
}

export const aiModelPricingApi = {
  status: () =>
    api.get<ModelsStatusResponse>('/api/admin/ai-model-pricing/models/status').then(r => r.data),

  history: (modelId: string, days = 30) =>
    api.get<ProbeHistoryPoint[]>('/api/admin/ai-model-pricing/models/probe-history', { params: { modelId, days } }).then(r => r.data),

  probeNow: (modelId: string) =>
    api.post(`/api/admin/ai-model-pricing/models/${modelId}/probe-now`).then(r => r.data),

  recommendations: () =>
    api.get<{ recommendations: TierRecommendation[] }>('/api/admin/ai-model-pricing/pricing/recommendations').then(r => r.data),

  updateMargin: (tierName: string, targetMarginPercent: number) =>
    api.patch('/api/admin/ai-model-pricing/pricing/margin', { tierName, targetMarginPercent }).then(r => r.data),

  guide: (tier: string) =>
    api.get<{ markdown: string }>('/api/admin/ai-model-pricing/pricing/guide', { params: { tier } }).then(r => r.data.markdown),
};
